import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useToast } from '../../../context/ToastContext';
import { formatDateOnly } from '../../../utils/dateUtils';
import { useDraggable } from '@dnd-kit/core';
import { useAuthState } from '../../../hooks/useAuthState';

interface Note {
  id: string;
  title: string;
  content: string;
  note_date: string;
  created_at: string;
  custom_tags: string[];
}

interface DraggableNoteProps {
  note: Note;
  darkMode: boolean;
}

const DraggableNote: React.FC<DraggableNoteProps> = ({ note, darkMode }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `note-${note.id}`,
    data: {
      type: 'note',
      cardId: note.id,
      title: note.title,
      preview: note.content
    }
  });

  // Extract note content into a reusable component
  const NoteContent = () => (
    <>
      <h3 className="font-medium mb-1">{note.title || 'Untitled Note'}</h3>
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
        {formatDateOnly(note.note_date)}
      </div>
      {note.custom_tags && note.custom_tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {note.custom_tags.map((tag, index) => (
            <span
              key={index}
              className={`text-xs px-2 py-0.5 rounded-full ${
                darkMode
                  ? 'bg-gray-600 text-gray-200'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`p-4 border-b ${
        darkMode ? 'border-gray-700 bg-gray-800 hover:bg-gray-700' : 'border-gray-200 bg-white hover:bg-gray-50'
      } cursor-grab active:cursor-grabbing transition-colors duration-200 touch-none`}
    >
      <NoteContent />
    </div>
  );
};

export const NotesTab: React.FC = () => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { user, isAuthenticated } = useAuthState();

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotes();
    }
  }, [isAuthenticated]);

  const fetchNotes = async () => {
    try {
      setIsLoading(true);
      if (!user) return;

      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', user.id)
        .order('note_date', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      showToast('Failed to load notes', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredNotes = notes.filter(note =>
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.custom_tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="h-full flex flex-col">
      {/* Search */}
      <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <input
          type="text"
          placeholder="Search notes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`w-full px-3 py-2 rounded-md border ${
            darkMode
              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
        />
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredNotes.length > 0 ? (
          filteredNotes.map(note => (
            <DraggableNote key={note.id} note={note} darkMode={darkMode} />
          ))
        ) : (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No notes found matching your search' : 'No notes available'}
          </div>
        )}
      </div>
    </div>
  );
}; 